import { useQuery, useMutation } from '@tanstack/react-query';
import { Dayjs } from 'dayjs';
import { QueryKey } from '../../enums/QueryKey';
import { queryClient } from '../../services/queryClient';
import dayjs from '@utils/dayjs';
import { useDeleteRecord } from './crudOperations';
import { useDispatchErrorNotification } from '../useDispatchErrorNotification';
import { useDispatchSuccessfullySavedRecord } from '../useDispatchSuccessfullySavedRecord';
import { deleteEvent, getEvents, getTotalEventsForAllLocations, postEvent } from '../../services/event';
import { appTimezone } from '../../config';

interface EventFilterParams {
    location: number;
    worktype: number;
    startDate: Dayjs;
    endDate: Dayjs;
}

export const getEventsQueryKey = (filterParams: EventFilterParams) => {
    const { location, worktype, startDate, endDate } = filterParams;

    // Use app timezone (Europe/Berlin) since events are displayed in German time
    const start = dayjs(startDate).tz(appTimezone).startOf('month').toISOString();
    const end = dayjs(endDate).tz(appTimezone).endOf('month').toISOString();

    return [
        QueryKey.Events,
        {
            start,
            end,
            ...(location && { location }),
            ...(worktype && { worktype }),
        },
    ];
};

export function useEvents(params: EventFilterParams) {
    const { location, worktype, startDate, endDate } = params;

    // Use app timezone (Europe/Berlin) since events are displayed in German time
    const start = dayjs(startDate).tz(appTimezone).startOf('month').toISOString();
    const end = dayjs(endDate).tz(appTimezone).endOf('month').toISOString();

    const { data } = useQuery({
        queryKey: getEventsQueryKey(params),
        queryFn: () =>
            getEvents({
                location,
                worktype,
                start,
                end,
            }),
        initialData: [],
        enabled:
            !!start &&
            !!end &&
            location !== undefined &&
            location !== null &&
            worktype !== undefined &&
            worktype !== null,
    });

    const { events, durationTotal, reductionTotal } = data as {
        events: unknown[];
        durationTotal: number;
        reductionTotal: number;
    };
    return { events, durationTotal, reductionTotal };
}

export function useDeleteEvent(filterParams: EventFilterParams) {
    return useDeleteRecord({
        deleteRecordFn: deleteEvent,
        fetchRecordsQueryKey: getEventsQueryKey(filterParams),
        recordName: 'Event',
    });
}

export function usePostEvent({ filterParams, onSuccess }: { filterParams: EventFilterParams; onSuccess: () => void }) {
    const { dispatchErrorNotification } = useDispatchErrorNotification();
    const { dispatchSuccessfullySavedRecord } = useDispatchSuccessfullySavedRecord();

    return useMutation({
        mutationFn: postEvent,
        onSuccess: () => {
            queryClient.invalidateQueries(getEventsQueryKey(filterParams));
            onSuccess();
            dispatchSuccessfullySavedRecord('Event');
        },
        onError: (error: Error) => {
            dispatchErrorNotification({ message: error.message });
        },
    });
}

export const useTotalEventsForAllLocations = () => {
    const { data } = useQuery({
        queryKey: [QueryKey.TotalEventsForAllLocations],
        queryFn: () => getTotalEventsForAllLocations(),
        initialData: [],
    });

    return [data];
};
